import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

/**
 * Wrap a route element to require authentication and, optionally, specific
 * permissions or roles.
 *
 * Examples:
 *   <ProtectedRoute><DashboardPage /></ProtectedRoute>
 *   <ProtectedRoute permission="accounts.manage"><CoaPage /></ProtectedRoute>
 *   <ProtectedRoute role="admin"><AdminPage /></ProtectedRoute>
 */
function ProtectedRoute({ children, permission, role, fallback = null }) {
  const { isAuthenticated, initializing, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  if (initializing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (permission) {
    const codes = Array.isArray(permission) ? permission : [permission];
    if (!hasPermission(...codes)) {
      return fallback || <Navigate to="/forbidden" replace />;
    }
  }

  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    if (!hasRole(...roles)) {
      return fallback || <Navigate to="/forbidden" replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
